import { useCallback, useEffect, useMemo, useState } from "react";
import {
  DashboardOrderListSchema,
  DashboardRestaurantListSchema,
  StorefrontOrderSchema,
  type DashboardRestaurant,
  type StorefrontOrder,
} from "@bytebite/schemas";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ACCEPTANCE_ETA_OPTIONS,
  canOperatorAct,
  formatAcceptanceCountdown,
  formatEtaLabel,
  orderStatusLabel,
} from "@/lib/acceptance";
import { kitchenActionLabel, kitchenWritePath, nextKitchenStatus } from "@/lib/kitchen";
import { ApiError, apiGet, apiPost } from "@/lib/api";
import { formatEuro } from "@/lib/money";
import { readStoredRestaurantId, storeRestaurantId } from "@/lib/restaurant-context";

const fieldClass =
  "border-input bg-background w-full rounded-md border px-3 py-2 text-sm shadow-sm";

const LIST_POLL_MS = 5_000;

export function OrdersPage() {
  const [restaurants, setRestaurants] = useState<DashboardRestaurant[]>([]);
  const [restaurantId, setRestaurantId] = useState<string | null>(null);
  const [orders, setOrders] = useState<StorefrontOrder[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const selected = useMemo(
    () => restaurants.find((restaurant) => restaurant.id === restaurantId) ?? null,
    [restaurantId, restaurants],
  );

  const loadOrders = useCallback(async (id: string) => {
    const payload = DashboardOrderListSchema.parse(
      await apiGet<unknown>(`/dashboard/restaurants/${id}/orders`),
    );
    setOrders(payload.orders);
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function boot() {
      try {
        const list = DashboardRestaurantListSchema.parse(
          await apiGet<unknown>("/dashboard/restaurants"),
        );
        if (cancelled) return;
        setRestaurants(list.restaurants);
        const stored = readStoredRestaurantId(list.restaurants[0]?.id ?? null);
        const next =
          list.restaurants.find((restaurant) => restaurant.id === stored)?.id ??
          list.restaurants[0]?.id ??
          null;
        setRestaurantId(next);
        if (next) {
          storeRestaurantId(next);
          await loadOrders(next);
        }
      } catch {
        if (!cancelled) setError("Could not load restaurants.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void boot();
    return () => {
      cancelled = true;
    };
  }, [loadOrders]);

  useEffect(() => {
    if (!restaurantId) return;
    const timer = window.setInterval(() => {
      void loadOrders(restaurantId).catch(() => {
        setError("Could not load these orders.");
      });
    }, LIST_POLL_MS);
    return () => window.clearInterval(timer);
  }, [loadOrders, restaurantId]);

  async function changeRestaurant(id: string) {
    setRestaurantId(id);
    storeRestaurantId(id);
    setError(null);
    try {
      await loadOrders(id);
    } catch {
      setError("Could not load these orders.");
    }
  }

  if (loading) {
    return <p className="text-muted-foreground text-sm">Loading orders…</p>;
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Orders</h1>
          <p className="text-muted-foreground text-sm">
            Pickup and delivery orders for the selected restaurant. Accept pending orders, then mark
            preparing, ready, and completed. Path context stays the tenant.
          </p>
        </div>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-muted-foreground">Restaurant</span>
          <select
            className={fieldClass}
            value={restaurantId ?? ""}
            onChange={(event) => void changeRestaurant(event.target.value)}
          >
            {restaurants.map((restaurant) => (
              <option key={restaurant.id} value={restaurant.id}>
                {restaurant.name}
              </option>
            ))}
          </select>
        </label>
      </div>
      {error ? <p className="text-destructive text-sm">{error}</p> : null}
      {selected && restaurantId ? (
        <OrdersList
          restaurantId={restaurantId}
          orders={orders}
          onRefresh={() => loadOrders(restaurantId)}
          onError={setError}
        />
      ) : (
        <p className="text-muted-foreground text-sm">No restaurants available.</p>
      )}
    </div>
  );
}

function OrdersList({
  restaurantId,
  orders,
  onRefresh,
  onError,
}: {
  restaurantId: string;
  orders: StorefrontOrder[];
  onRefresh: () => Promise<void>;
  onError: (message: string | null) => void;
}) {
  if (orders.length === 0) {
    return <p className="text-muted-foreground text-sm">No orders yet.</p>;
  }

  return (
    <div className="flex flex-col gap-4">
      {orders.map((order) => (
        <OrderCard
          key={order.id}
          restaurantId={restaurantId}
          order={order}
          onRefresh={onRefresh}
          onError={onError}
        />
      ))}
    </div>
  );
}

function OrderCard({
  restaurantId,
  order,
  onRefresh,
  onError,
}: {
  restaurantId: string;
  order: StorefrontOrder;
  onRefresh: () => Promise<void>;
  onError: (message: string | null) => void;
}) {
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [etaMinutes, setEtaMinutes] = useState<(typeof ACCEPTANCE_ETA_OPTIONS)[number]>(30);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (order.orderStatus !== "pending") return;
    const timer = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [order.orderStatus]);

  const actionable = canOperatorAct(order, nowMs);
  const kitchenStep = nextKitchenStatus(order);

  async function mutate(path: string, body: unknown, conflictMessage: string) {
    setPending(true);
    onError(null);
    try {
      StorefrontOrderSchema.parse(await apiPost<unknown>(path, body));
      await onRefresh();
    } catch (cause) {
      if (cause instanceof ApiError && cause.status === 409) {
        onError(conflictMessage);
        await onRefresh().catch(() => undefined);
      } else if (cause instanceof ApiError && cause.status === 404) {
        onError("Order not found for this restaurant.");
      } else {
        onError("The order could not be updated.");
      }
    } finally {
      setPending(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-mono text-base">{order.id}</CardTitle>
        <CardDescription>
          {order.customerName} · {order.customerPhone} · {orderStatusLabel(order)}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <ul className="flex flex-col gap-2 text-sm">
          {order.items.map((item) => (
            <li key={item.id} className="flex justify-between gap-3">
              <span>
                {item.variantName ? `${item.name} · ${item.variantName}` : item.name} ×{" "}
                {item.quantity}
              </span>
              <span>{formatEuro(item.lineTotalCents)}</span>
            </li>
          ))}
        </ul>
        <p className="text-sm font-medium">Total {formatEuro(order.totalCents)}</p>
        {order.fulfillmentType === "delivery" ? (
          <div className="text-muted-foreground flex flex-col gap-1 text-sm">
            <p>Delivery · {order.address?.formattedLine ?? "Address missing"}</p>
            <p>
              Subtotal {formatEuro(order.subtotalCents)} · Fee {formatEuro(order.deliveryFeeCents)}
            </p>
          </div>
        ) : (
          <p className="text-muted-foreground text-sm">Pickup</p>
        )}
        {order.note ? <p className="text-muted-foreground text-sm">Note: {order.note}</p> : null}
        {order.orderStatus === "pending" ? (
          <p className="text-sm">
            Accept within {formatAcceptanceCountdown(order.acceptanceDeadlineAt, nowMs)}
          </p>
        ) : null}
        {actionable ? (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-muted-foreground">ETA (minutes)</span>
              <select
                className={fieldClass}
                value={etaMinutes}
                onChange={(event) =>
                  setEtaMinutes(
                    Number(event.target.value) as (typeof ACCEPTANCE_ETA_OPTIONS)[number],
                  )
                }
              >
                {ACCEPTANCE_ETA_OPTIONS.map((value) => (
                  <option key={value} value={value}>
                    {formatEtaLabel(value)}
                  </option>
                ))}
              </select>
            </label>
            <div className="flex gap-2">
              <Button
                type="button"
                disabled={pending}
                onClick={() =>
                  void mutate(
                    `/dashboard/restaurants/${restaurantId}/orders/${order.id}/accept`,
                    { etaMinutes },
                    "This order is no longer pending.",
                  )
                }
              >
                {pending ? "Saving…" : "Accept"}
              </Button>
              <Button
                type="button"
                variant="destructive"
                disabled={pending}
                onClick={() =>
                  void mutate(
                    `/dashboard/restaurants/${restaurantId}/orders/${order.id}/reject`,
                    {},
                    "This order is no longer pending.",
                  )
                }
              >
                Reject
              </Button>
            </div>
          </div>
        ) : null}
        {kitchenStep ? (
          <div className="flex gap-2">
            <Button
              type="button"
              disabled={pending}
              onClick={() =>
                void mutate(
                  kitchenWritePath(restaurantId, order.id, kitchenStep),
                  {},
                  "This kitchen step is no longer valid.",
                )
              }
            >
              {pending ? "Saving…" : kitchenActionLabel(kitchenStep)}
            </Button>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
